<template>
  <nav class="pagination-container" aria-label="分页">
    <span class="pagination-total">共 {{ total }} 条</span>
    <label class="page-size-label">
      每页
      <select :value="pageSize" @change="changePageSize">
        <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
      </select>
      条
    </label>
    <NativeButton :disabled="currentPage <= 1" @click="setCurrentPage(currentPage - 1)">
      上一页
    </NativeButton>
    <span>{{ currentPage }} / {{ pageCount }}</span>
    <NativeButton
      :disabled="currentPage >= pageCount"
      @click="setCurrentPage(currentPage + 1)"
    >
      下一页
    </NativeButton>
    <label class="jump-label">
      跳至
      <input
        type="number"
        min="1"
        :max="pageCount"
        :value="currentPage"
        @change="jumpToPage"
      />
      页
    </label>
  </nav>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    total: number;
    pageSizes?: number[];
    layout?: string;
  }>(),
  { pageSizes: () => [5, 10, 20, 50, 100] },
);

const emit = defineEmits<{
  (event: "size-change", value: number): void;
  (event: "current-change", value: number): void;
}>();

const currentPage = defineModel<number>("currentPage", { required: true });
const pageSize = defineModel<number>("pageSize", { required: true });
const pageCount = computed(() =>
  Math.max(1, Math.ceil(props.total / pageSize.value)),
);

const setCurrentPage = (page: number) => {
  const boundedPage = Math.min(Math.max(1, page), pageCount.value);
  currentPage.value = boundedPage;
  emit("current-change", boundedPage);
};

const changePageSize = (event: Event) => {
  const size = Number((event.target as HTMLSelectElement).value);
  pageSize.value = size;
  emit("size-change", size);
};

const jumpToPage = (event: Event) => {
  const page = Number((event.target as HTMLInputElement).value);
  if (Number.isFinite(page)) setCurrentPage(page);
};
</script>

<style scoped lang="scss">
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px 0;
  color: #475569;
  font-size: 0.875rem;
}

.pagination-container select,
.pagination-container input {
  min-height: 34px;
  padding: 4px 7px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
}

.pagination-container input {
  width: 58px;
}

.page-size-label,
.jump-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

@media (max-width: 640px) {
  .pagination-container {
    justify-content: center;
  }
}
</style>
